import React, { useContext, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Dropdown, ProgressBar } from 'react-bootstrap';
import { useHistory } from 'react-router-dom';

import CsLineIcons from 'cs-line-icons/CsLineIcons';
import { settingsChangeColor } from 'settings/settingsSlice';
import { GetEventJudgeStore } from 'SessionState/Judge/EventJudgeStore';
import { IReduxState } from 'ReduxState';

import JudgePreferencesModal from 'layout/right-buttons/JudgePreferencesModal';
import { JudgeDataContext } from 'data/JudgeDataProvider';
import JudgeTabulatorHelpMessage from 'views/judgeportal/JudgeTabulatorHelpMessage';

type JudgeNavIconMenuProps = {
  onHome?: () => void;
  onLogout?: () => void;
};

const JudgeNavIconMenu: React.FC<JudgeNavIconMenuProps> = ({ onHome, onLogout }) => {
  const { color } = useSelector((state: IReduxState) => state.settings);
  const dispatch = useDispatch();
  const history = useHistory();

  const {
  judgeId,
  pageantAvailableAOC = [],
  allFetchedScores = {},
  registeredContestants = [],
  isEnteredForAOC,
} = useContext(JudgeDataContext) as any;

  const judgeStore = GetEventJudgeStore();
  const judge = judgeStore.CurrentJudge;

  const judgeName = judge?.judgeName ?? '';
  const judgeLabel = judge?.judgeLabel ?? '';
  const accessCode = judge?.accessCodeLast4 ?? '';
  const status = judge ? 'Logged In' : 'Not Logged In';

  const eventName = judge?.eventName ?? '';

  const getContestantId = (contestant: any): number =>
  Number(
    contestant?.registeredContestantId ??
      contestant?.id ??
      contestant?.registeredPageantContestantId ??
      0,
  );

const getAocScoreCategories = (aoc: any): any[] =>
  aoc?.aocScoringCategories ??
  aoc?.aocScoreCategories ??
  aoc?.scoreCategories ??
  [];

const normalizeAocIdFromScoreRow = (row: any): number =>
  Number(row?.aocId ?? row?.pageantAvailableEventId ?? row?.pageantAvailableAocId ?? 0);

const normalizeCategoryIdFromScoreRow = (row: any): number =>
  Number(row?.aocScoreCategoryId ?? row?.scoreCategoryId ?? row?.categoryId ?? 0);

const getScoreRowsForContestant = (contestantId: number): any[] =>
  allFetchedScores?.[Number(contestantId)] ?? [];

const scoreRowMatchesJudge = (row: any): boolean => {
  const rawJudgeId = row?.judgeId ?? row?.eventJudgeId;
  if (rawJudgeId == null || rawJudgeId === '') return true;
  return Number(rawJudgeId) === Number(judgeId);
};

const isContestantCompleteForAoc = (contestant: any, aoc: any): boolean => {
  const contestantId = getContestantId(contestant);
  const aocId = Number(aoc?.id ?? 0);

  if (!contestantId || !aocId || !isEnteredForAOC?.(contestant, aocId)) {
    return false;
  }

  const categories = getAocScoreCategories(aoc);
  const rows = getScoreRowsForContestant(contestantId).filter(
    (row) =>
      normalizeAocIdFromScoreRow(row) === aocId &&
      scoreRowMatchesJudge(row),
  );

  if (categories.length === 0) {
    return rows.some((row) => row?.score !== null && row?.score !== undefined);
  }

  return categories.every((category: any) =>
    rows.some(
      (row) =>
        normalizeCategoryIdFromScoreRow(row) === Number(category?.id ?? 0) &&
        row?.score !== null &&
        row?.score !== undefined,
    ),
  );
};

const eligibleContestantAocPairs = useMemo(() => {
  const pairs: Array<{ contestant: any; aoc: any; aocId: number }> = [];

  for (const contestant of registeredContestants ?? []) {
    for (const aoc of pageantAvailableAOC ?? []) {
      const aocId = Number(aoc?.id ?? 0);

      if (aocId && isEnteredForAOC?.(contestant, aocId)) {
        pairs.push({ contestant, aoc, aocId });
      }
    }
  }

  return pairs;
}, [registeredContestants, pageantAvailableAOC, isEnteredForAOC]);

const getAocProgressForPairs = (pairs: Array<{ contestant: any; aoc: any }>) => {
  const total = pairs.length;
  const completed = pairs.reduce(
    (sum, pair) => sum + (isContestantCompleteForAoc(pair.contestant, pair.aoc) ? 1 : 0),
    0,
  );

  return {
    completed,
    total,
    percent: total === 0 ? 0 : Math.round((completed / total) * 100),
  };
};

const overallProgress = useMemo(() => {
  return getAocProgressForPairs(eligibleContestantAocPairs);
}, [eligibleContestantAocPairs, allFetchedScores, judgeId]);

const progressByAoc = useMemo(() => {
  return (pageantAvailableAOC ?? []).map((aoc: any) => {
    const aocId = Number(aoc?.id ?? 0);
    const aocPairs = eligibleContestantAocPairs.filter((pair) => pair.aocId === aocId);
    const progress = getAocProgressForPairs(aocPairs);

    return {
      id: aocId,
      name: aoc?.name ?? 'AOC',
      ...progress,
    };
  });
}, [pageantAvailableAOC, eligibleContestantAocPairs, allFetchedScores, judgeId]);

  const [isShowSettingsModal, setIsShowSettingsModal] = useState(false);
  const [showHelpMessageModal, setShowHelpMessageModal] = useState(false);

    const showSettingsModal = () => {
    setIsShowSettingsModal(true);
  };
  const closeSettingsModal = () => {
    setIsShowSettingsModal(false);
    document.documentElement.click();
  };
 
  const handleHome = () => {
    if (onHome) {
      onHome();
      return;
    }

    if (judge?.id && judge?.eventId) {
      history.push(`/judge/eventhome/${judge.id}/${judge.eventId}`);
    }
  };

const handleLogout = async () => {
  if (onLogout) {
    await onLogout();
  }

  judgeStore.logoutJudge();
  history.push('/judge/login');
};

  const handleHelp = () => {
    history.push('/judge/help');
  };

  const handleToggleTheme = () => {
    dispatch(
      settingsChangeColor(
        color.includes('light')
          ? color.replace('light', 'dark')
          : color.replace('dark', 'light')
      )
    );
  };

  return (
    <div className="d-flex align-items-center gap-3">
      <div className="d-none d-md-block text-end">
        <div className="fw-bold text-truncate">{judgeLabel}</div>

        <div className="small text-muted">
          {judgeName && <span>{judgeName}</span>}
          {judgeName && accessCode && <span className="mx-1">|</span>}
          {accessCode && <span>Code: {accessCode}</span>}
  

        </div>
       
      </div>
<div className='cta-3 text-muted'>
  <strong>{overallProgress.percent}%</strong>
</div>
      <button
        type="button"
        className="btn btn-link p-1 text-body"
        title="Home"
        onClick={handleHome}
      >
        <CsLineIcons icon="home" size="20" />
      </button>

      <Dropdown align="end">
        <Dropdown.Toggle
          variant="link"
          className="p-1 text-body border-0 shadow-none"
        >
          <CsLineIcons icon="user" size="20" />
        </Dropdown.Toggle>

        <Dropdown.Menu>
          <div className="px-3 py-2">
            <div className="fw-bold">{judgeName}</div>
            {judgeLabel && <div className="small text-muted">{judgeLabel}</div>}
            {accessCode && (
              <div className="small text-muted">Access Code: {accessCode}</div>
            )}
             <div className="fw-bold text-truncate">{eventName}</div>
          </div>

          <Dropdown.Divider />

          <Dropdown.Item onClick={handleHome}>
            <CsLineIcons icon="home" size="16" className="me-2" />
            Home
          </Dropdown.Item>
          <Dropdown.Item onClick={() => setShowHelpMessageModal(true)}>
  <CsLineIcons icon="message" size="16" className="me-2" />
  Message Tabulator
</Dropdown.Item>

          <Dropdown.Item onClick={() => showSettingsModal()}>
            <CsLineIcons icon="moon" size="16" className="me-2" />
            Visual Preferences
          </Dropdown.Item>

          {progressByAoc.length > 0 && (
  <div className="mt-2 p-2 border-top">
    {progressByAoc.map((aoc) => (
      <div key={aoc.id} className="mb-2">
        <div className="d-flex justify-content-between small">
          <span className="text-truncate me-2">{aoc.name}</span>
          <span className="fw-bold">{aoc.percent}%</span>
        </div>

        <ProgressBar
          now={aoc.percent}
          variant={aoc.percent === 100 ? 'success' : aoc.percent > 0 ? 'warning' : 'secondary'}
          style={{ height: 4 }}
        />
      </div>
    ))}
  </div>
)}

          {/* <Dropdown.Item onClick={handleHelp}>
            <CsLineIcons icon="help" size="16" className="me-2" />
            Help
          </Dropdown.Item> */}

          <Dropdown.Divider />

          <Dropdown.Item className="text-danger" onClick={handleLogout}>
            <CsLineIcons icon="logout" size="16" className="me-2" />
            Log Out
          </Dropdown.Item>
        </Dropdown.Menu>
      </Dropdown>
              <JudgePreferencesModal show={isShowSettingsModal} handleClose={closeSettingsModal} />
              <JudgeTabulatorHelpMessage
  show={showHelpMessageModal}
  onHide={() => setShowHelpMessageModal(false)}
/>
    </div>
  );
};

export default React.memo(JudgeNavIconMenu);